import { Circle, DriveFileMoveOutlined } from '@mui/icons-material'
import { FC, useEffect, useMemo, useRef } from 'react'
import { Grid, Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'

import { BEIGE_700 } from 'constants/styling/theme'
import { CreativeUploadWizardStep } from './_main/CreativeUploadWizard.controller'
import { DynamicFlowStep } from '../DynamicFlow'
import { FileAPIUpload } from '../FileAPIUpload'
import { ImageItem } from './ImageItem.comonent'
import { MUIButton } from '../MUIButton'
import { MUITooltip } from '../MUITooltip'
import { SectionedBorderBox } from '../SectionedBorderBox'
import { SystemMessage } from '../SystemMessage'
import { externalNavigate } from 'utils/helpers'
import { useCreativeUploadWizard } from './_main/CreativeUploadWizard.context'
import { useSnackbar } from 'components/contexts/SnackbarService.context'

export const RequiredVisualsStep: FC = () => {
  const { t } = useTranslation(['ct_upload_wizard'])
  const {
    fileCtrl,
    requiredVisualAmount,
    isOverRequiredLimit,
    assignmentData
  } = useCreativeUploadWizard()
  const { spawnWarningToast } = useSnackbar()

  const filesToDisplay = useMemo(() => fileCtrl.filesByTag[CreativeUploadWizardStep.REQUIRED_VISUALS] ?? [], [fileCtrl.filesByTag])

  const hasBeenWarned = useRef(false)
  useEffect(() => {
    if (isOverRequiredLimit && !hasBeenWarned.current) {
      spawnWarningToast(
        t('upload_limit_warning_message', { count: requiredVisualAmount }),
        {
          title: t('upload_limit_warning_title'),
          customId: 'CT_UPLOAD_OVER_LIMIT_WARNING',
          timeout: 12000,
        }
      )

      hasBeenWarned.current = true
    }

    if (!isOverRequiredLimit) hasBeenWarned.current = false

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverRequiredLimit, filesToDisplay.length])

  return (
    <DynamicFlowStep
      stepKey={CreativeUploadWizardStep.REQUIRED_VISUALS}
      isValid={true}
    >
      <Stack alignItems="center" gap={3} padding="4rem 0">

        <Typography
          variant="h2"
          textAlign="left"
          width="90%"
          maxWidth="100rem"
          lineHeight="normal"
          fontWeight={600}
        >
          {t('required_step.title')}
        </Typography>

        <SystemMessage
          sx={{ width: '90%', maxWidth: '100rem' }}
          message={(
            <Typography fontSize="1.4rem" component="span">
              <Trans
                t={t}
                i18nKey="required_step.info_message"
                components={{
                  list: <ul style={{ padding: '0 2rem' }} />,
                  item: <li />,
                }}
                values={{ count: requiredVisualAmount }}
              >
                &nbsp;
              </Trans>
            </Typography>
          )}
        />

        <SectionedBorderBox
          width="90%"
          maxWidth="100rem"
          title={
            <Typography variant="h3" fontWeight={500} lineHeight="1.3">
              <Trans
                t={t}
                i18nKey="required_step.upload_title"
                components={{
                  divider: <Circle fontSize="small" htmlColor={BEIGE_700} />
                }}
                values={{
                  uploaded: filesToDisplay.length ?? 0,
                  limit: requiredVisualAmount,
                }}
              >
                &nbsp;
              </Trans>
            </Typography>
          }
          position="relative"
          p={2}
        >
          <MUIButton
            type="secondaryBorder"
            size="sm"
            onClick={() => assignmentData?.creativeInstructionsUrl && externalNavigate(assignmentData.creativeInstructionsUrl)}
            sx={{
              position: 'absolute',
              top: '1.5rem',
              right: '2rem',
            }}
          >
            {t('mandatory_instructions')}
          </MUIButton>

          <Stack gap={2}>

            <FileAPIUpload
              showFileListing={false}
              files={filesToDisplay}
              acceptedFileTypes="any"
              uploadHandler={(acceptedFiles) => fileCtrl.uploadFiles(acceptedFiles, {
                tag: CreativeUploadWizardStep.REQUIRED_VISUALS,
                onSettled: (successIds) => {
                  fileCtrl.loadFiles(successIds)
                },
              })}
            />

            <Grid container spacing={1}>
              {!!filesToDisplay.length && filesToDisplay.sort((fileA, fileB) => fileB.order - fileA.order).map((file) => (
                <Grid item key={file.id} xs={3}>
                  <ImageItem
                    config={{ allowDelete: true }}
                    file={file}
                    fileCtrl={fileCtrl}
                    extraActionsSlot={filesToDisplay.length > requiredVisualAmount &&
                      <MUITooltip content={t('required_step.move_to_additional')}>
                        <MUIButton size="sm" isIconButton onClick={() => fileCtrl.editFile(file.id, { tag: CreativeUploadWizardStep.ADDITIONAL_VISUALS })}>
                          <DriveFileMoveOutlined fontSize="large" />
                        </MUIButton>
                      </MUITooltip>
                    }
                  />
                </Grid>
              ))}
            </Grid>

          </Stack>
        </SectionedBorderBox>

      </Stack>
    </DynamicFlowStep>
  )
}
